// VERSION: 2025-01-07-SCALE-FIX-v2
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import useZoomPan, { ZOOM_MIN, ZOOM_MAX, ZOOM_PASO } from '../hooks/useZoomPan';
import usePaginasNitidas from '../hooks/usePaginasNitidas';

const VACIO = []; // identidad estable: evita reejecutar efectos mientras no hay páginas

/*
 * Rasterizado de las páginas.
 *
 * Cuántas a la vez: rasterizar las N páginas de golpe mantiene vivos N canvas simultáneos
 * (~8 MB cada uno a 2x), y con un boletín de 28 páginas eso son ~215 MB de pico que ahogan la
 * pestaña. Limitarlo no lo hace más lento: pdf.js parsea en su worker pero dibuja en el hilo
 * principal, así que las páginas se turnan igual.
 */
const CONCURRENCIA_RENDER = 3;

/*
 * Formato de la imagen base. PNG es sin pérdida y, en páginas con fotos, enorme.
 * `image/jpeg` con calidad ~0.9 baja el peso alrededor de un orden de magnitud; ahora que al
 * ampliar se re-rasteriza nítido desde el PDF (usePaginasNitidas), la imagen base sólo se ve
 * hasta 1.25x. Cambiar estas dos constantes es todo lo que hace falta para probarlo.
 */
const FORMATO_PAGINA = 'image/png';
const CALIDAD_PAGINA = undefined;

// Los objectURL viven hasta que se revocan: sin esto, cada publicación visitada deja sus
// imágenes en memoria durante toda la sesión
const revocarImagenes = (paginas) => {
  (paginas || []).forEach((p) => {
    if (p && typeof p.imageUrl === 'string' && p.imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(p.imageUrl);
    }
  });
};

// Configurar el worker de PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

// Componente de página separado para no recrearse en cada render
const Page = React.forwardRef(({ pageData, className, flipBookRef }, ref) => {
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [linkBoxes, setLinkBoxes] = useState([]); // almacenar cajas en % relativos a la imagen base
  const [activeVideo, setActiveVideo] = useState(null); // embed URL para modal de video
  const computedOnceRef = useRef(false); // bandera para calcular solo una vez al montar
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  // Detectar YouTube y convertir a URL de embed. Si no es YouTube, abrir en pestaña nueva.
  const getYouTubeEmbedUrl = useCallback((url) => {
    if (!url) return null;
    try {
      const u = new URL(url, window.location.href);
      const host = u.hostname.toLowerCase();
      let id = null;
      if (host.includes('youtube.com')) {
        // ejemplo: /watch?v=ID
        id = u.searchParams.get('v');
      } else if (host === 'youtu.be') {
        // ejemplo: youtu.be/ID
        id = u.pathname.slice(1);
      }
      if (!id) return null;
      const embed = `https://www.youtube.com/embed/${encodeURIComponent(id)}?rel=0&showinfo=0&autoplay=1`;
      return embed;
    } catch (e) {
      return null;
    }
  }, []);

  const handleLinkClick = useCallback((link) => {
    // Manejar enlaces internos (destinos dentro del PDF)
    if (link.dest && flipBookRef?.current) {
      try {
        // Navegar a la página de destino
        const targetPage = link.destPageNumber - 1; // flipbook usa índice base 0
        flipBookRef.current.pageFlip().flip(targetPage);
        return;
      } catch (e) {
        console.warn('Error al navegar a página interna:', e);
      }
    }
    
    const url = link.url || link;
    // Si es enlace YouTube, abrir en overlay con iframe
    const embed = getYouTubeEmbedUrl(url);
    if (embed) {
      try {
        setActiveVideo(embed);
        return;
      } catch (e) {
        // si falla, caer al comportamiento por defecto
      }
    }
    // Abrir el link en una nueva pestaña para otros dominios
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [getYouTubeEmbedUrl, flipBookRef]);

  // Mantener el tamaño mostrado del <img> sincronizado (wrapper igual al img)
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const update = () => {
      const w = img.clientWidth || img.width || 0;
      const h = img.clientHeight || img.height || 0;
      setDisplaySize({ w, h });
      // permitir recalcular cajas si cambió tamaño
      computedOnceRef.current = false;
    };
    update();
    // Observar cambios de tamaño del img
    let ro;
    try {
      ro = new ResizeObserver(() => {
        update();
      });
      ro.observe(img);
    } catch (e) {
      // ResizeObserver puede no estar disponible en algunos entornos
      window.addEventListener('resize', update);
    }
    return () => {
      try { if (ro && img) ro.unobserve(img); } catch(_) {}
      try { window.removeEventListener('resize', update); } catch(_) {}
    };
  }, [imgRef]);

  // Calcular overlays UNA SOLA VEZ al cargar la imagen (usando dimensiones naturales)
  const computeLinkBoxesOnce = useCallback(() => {
    if (!pageData || !pageData.links || !imgRef.current || computedOnceRef.current) return;
    const imgEl = imgRef.current;

    // Tamaño real mostrado (no natural) dentro del flipbook
    const displayW = imgEl.clientWidth;
    const displayH = imgEl.clientHeight;
    if (displayW < 10 || displayH < 10) return; // esperar layout

    const baseW = pageData.width;   // ancho del canvas original renderizado
    const baseH = pageData.height;  // alto del canvas original renderizado
    if (!baseW || !baseH) return;

    const scaleX = displayW / baseW;
    const scaleY = displayH / baseH;

    const newBoxes = pageData.links.map(link => {
      if (!link.viewportRect) return null;
      const [x1, y1, x2, y2] = link.viewportRect; // suponemos coordenadas top-left pero validamos
      // Detectar si viene invertido verticalmente: si y1 > baseH o valores negativos raros
      let topSrc = y1;
      let bottomSrc = y2;
      if (y1 > y2) { // swap si vienen invertidos
        topSrc = y2; bottomSrc = y1;
      }
      // Si parece estar en sistema invertido (origin bottom-left) porque topSrc > baseH/2 y bottomSrc > baseH
      if (bottomSrc > baseH) {
        // Reinterpretar como coordenadas bottom-left: newTop = baseH - maxY
        const maxY = Math.max(y1, y2);
        const minY = Math.min(y1, y2);
        const hRaw = Math.abs(y2 - y1);
        topSrc = baseH - maxY;
        bottomSrc = topSrc + hRaw;
      }
      // Clamp
      topSrc = Math.max(0, Math.min(baseH, topSrc));
      bottomSrc = Math.max(topSrc, Math.min(baseH, bottomSrc));
      const leftSrc = Math.min(x1, x2);
      const rightSrc = Math.max(x1, x2);
      const wSrc = Math.max(1, rightSrc - leftSrc);
      const hSrc = Math.max(1, bottomSrc - topSrc);

      // Convertir desde coordenadas del canvas a coordenadas dentro del img DOM (tienen offset dentro de imgRect)
      const left = leftSrc * scaleX;
      const top = topSrc * scaleY;
      const w = wSrc * scaleX;
      const h = hSrc * scaleY;

      // Si el overlay queda fuera verticalmente, fallback a porcentajes previos (si existen)
      if ((top + h) > displayH + 2 || top < -2) {
        if (link.pct) {
          const boxPct = {
            url: link.url,
            dest: link.dest,
            destPageNumber: link.destPageNumber,
            style: {
              position: 'absolute',
              left: `${link.pct.left}%`,
              top: `${link.pct.top}%`,
              width: `${link.pct.width}%`,
              height: `${link.pct.height}%`,
              boxSizing: 'border-box',
              zIndex: 10
            },
            className: 'link-overlay'
          };
          console.warn('[link-box-fallback-pct] page', pageData.pageNumber, link.url, { pct: link.pct, displayH, baseH });
      // debug info removed: previously pushed to window.__linkDebug
          return boxPct;
        }
      }

      const box = {
        url: link.url,
        dest: link.dest,
        destPageNumber: link.destPageNumber,
        style: {
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${w}px`,
          height: `${h}px`,
          boxSizing: 'border-box',
          zIndex: 10
        },
        className: 'link-overlay'
      };
      console.debug('[link-box-px] page', pageData.pageNumber, link.url, { baseW, baseH, displayW, displayH, scaleX: scaleX.toFixed(3), scaleY: scaleY.toFixed(3), srcRect: link.viewportRect, normalized: { leftSrc, topSrc, wSrc, hSrc }, placed: { left, top, w, h } });
      // debug info removed: previously pushed to window.__linkDebug
      return box;
    }).filter(Boolean);

    setLinkBoxes(newBoxes);
    computedOnceRef.current = true;
  }, [pageData]);

  // Recalcular cuando cambie displaySize (por ejemplo al redimensionar el flipbook)
  useEffect(() => {
    computedOnceRef.current = false;
    setLinkBoxes([]);
    computeLinkBoxesOnce();
  }, [computeLinkBoxesOnce, displaySize]);

  // Resetear la bandera cuando cambia pageData (página diferente)
  useEffect(() => {
    computedOnceRef.current = false;
    setLinkBoxes([]);
  }, [pageData]);

  // Compute once on mount or when image loads
  useEffect(() => {
    computeLinkBoxesOnce();
  }, [computeLinkBoxesOnce]);  return (
    <div ref={(el) => { containerRef.current = el; if (typeof ref === 'function') ref(el); else if (ref) ref.current = el; }} className={`page-content ${className || ''}`} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      {pageData && (
        <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%', display: 'inline-block' }}>
          <img
            ref={imgRef}
            src={pageData.imageUrl}
            alt={`Página ${pageData.pageNumber}`}
            /* punto de enganche para cambiar a la versión en alta al ampliar (usePaginasNitidas) */
            data-pagina={pageData.pageNumber}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
            onLoad={() => {
              // Computar cajas una vez cuando la imagen haya cargado
              requestAnimationFrame(() => computeLinkBoxesOnce());
            }}
          />

          {/* Capa de links sobre la imagen: porcentajes relativos a la imagen, no al contenedor padre */}
          {linkBoxes && linkBoxes.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none' // Permitir clicks salvo en los hijos
              }}
            >
              {linkBoxes.map((box, idx) => (
                <a
                  key={idx}
                  href={box.url || '#'}
                  target={box.dest ? undefined : "_blank"}
                  rel={box.dest ? undefined : "noopener noreferrer"}
                  // Prevent the flipbook from receiving pointer/click events when interacting with links
                  onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); try { e.nativeEvent && e.nativeEvent.stopImmediatePropagation && e.nativeEvent.stopImmediatePropagation(); } catch(_){}; handleLinkClick(box); }}
                  onPointerDownCapture={(e) => { e.stopPropagation(); }}
                  onMouseDownCapture={(e) => { e.stopPropagation(); }}
                  onTouchStartCapture={(e) => { e.stopPropagation(); }}
                  onPointerDown={(e) => { e.stopPropagation(); }}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onTouchStart={(e) => { e.stopPropagation(); }}
                  style={box.style}
                  className={box.className}
                  title={box.dest ? `Ir a página ${box.destPageNumber}` : box.url}
                  aria-label={box.dest ? `Enlace interno a página ${box.destPageNumber}` : `Link a ${box.url}`}
                />
              ))}
            </div>
          )}

          {/* Overlay modal para reproducir videos embebidos (YouTube) dentro de la página */}
          {activeVideo && (
            <div
              role="dialog"
              aria-modal="true"
              className="video-overlay"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setActiveVideo(null); }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.65)',
                zIndex: 99999,
                pointerEvents: 'auto'
              }}
            >
              <div
                onClick={(e) => { e.stopPropagation(); }}
                style={{
                  width: '80%',
                  maxWidth: '1100px',
                  height: '60%',
                  maxHeight: '720px',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <iframe
                  title="YouTube video"
                  src={activeVideo}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 6 }}
                />
                <button
                  aria-label="Cerrar video"
                  onClick={() => setActiveVideo(null)}
                  style={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    background: '#111',
                    color: '#fff',
                    border: 'none',
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

  const Flipbook = ({ pdfUrl }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fsNativo, setFsNativo] = useState(false);      // pantalla completa real (API Fullscreen)
  const [fsSimulado, setFsSimulado] = useState(false);  // respaldo cuando el navegador no la concede (iOS/Safari)
  const [area, setArea] = useState({ w: 0, h: 0 });     // espacio realmente disponible para el libro
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [areaEl, setAreaEl] = useState(null);           // nodo del hueco, vía ref de callback
  const [paginaActual, setPaginaActual] = useState(0);  // índice que reporta la librería, para la barra
  const flipBookRef = useRef();
  const containerRef = useRef(null);
  const paginaActualRef = useRef(0);
  const pdfDocRef = useRef(null);                       // documento vivo, para rasterizar en alta al ampliar
  const imagenesRef = useRef([]);                       // páginas en pantalla, para revocar sus objectURL
  const cargaRef = useRef(0);                           // token de carga: descarta las que quedaron obsoletas

  const isFullscreen = fsNativo || fsSimulado;

  // Capa de zoom: transform sobre la caja del libro, sin tocar la geometría de la librería
  const {
    refViewport,
    zoom,
    conZoom,
    arrastrando,
    estiloTransform,
    zoomEn,
    reiniciar: reiniciarZoom,
    reajustar: reajustarZoom,
  } = useZoomPan();

  // Al cambiar de página el zoom vuelve al 100% (mismo comportamiento que Issuu)
  const irAPagina = useCallback((accion) => {
    reiniciarZoom();
    try { flipBookRef.current?.pageFlip()?.[accion](); } catch (_) {}
  }, [reiniciarZoom]);

  // Volver al principio en un click. `flip(0)` no rehace todos los pliegos: la librería se
  // coloca en el pliego contiguo y anima una sola vuelta de página.
  const irAlInicio = useCallback(() => {
    reiniciarZoom();
    try { flipBookRef.current?.pageFlip()?.flip(0); } catch (_) {}
  }, [reiniciarZoom]);

  // Detectar si el dispositivo es móvil
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (pdfUrl) {
      paginaActualRef.current = 0;
      setPaginaActual(0);
      loadPDF(pdfUrl);
    }
  }, [pdfUrl]);

  // Al desmontar: invalidar la carga en curso y soltar las imágenes de las páginas
  useEffect(() => () => {
    cargaRef.current += 1;
    revocarImagenes(imagenesRef.current);
    imagenesRef.current = [];
  }, []);

  // Medir el hueco real que queda para el libro (flechas y barra ya descontadas por el layout flex).
  // Esta medida es la única fuente de verdad del tamaño: nada se estima con vh ni con restas manuales.
  useEffect(() => {
    const el = areaEl;
    if (!el) return undefined;
    const medir = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setArea((prev) => (Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1 ? prev : { w, h }));
    };
    medir();
    let ro = null;
    try {
      ro = new ResizeObserver(medir);
      ro.observe(el);
    } catch (_) {
      // ResizeObserver puede no estar disponible: el listener de resize cubre el caso
    }
    window.addEventListener('resize', medir);
    window.addEventListener('orientationchange', medir);
    const t = setTimeout(medir, 250); // el layout puede tardar un frame extra en asentarse
    return () => {
      clearTimeout(t);
      try { if (ro) ro.disconnect(); } catch (_) {}
      window.removeEventListener('resize', medir);
      window.removeEventListener('orientationchange', medir);
    };
  }, [areaEl]);

  // Sincronizar estado al cambiar fullscreen (ESC, F11, etc.)
  useEffect(() => {
    const handleFsChange = () => {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      setFsNativo(!!fsElement && fsElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    // vendor prefijos por soporte amplio
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    document.addEventListener('mozfullscreenchange', handleFsChange);
    document.addEventListener('MSFullscreenChange', handleFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
      document.removeEventListener('mozfullscreenchange', handleFsChange);
      document.removeEventListener('MSFullscreenChange', handleFsChange);
    };
  }, []);

  // En pantalla completa simulada no hay ESC del navegador: lo manejamos nosotros
  useEffect(() => {
    if (!fsSimulado) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFsSimulado(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fsSimulado]);

  // Entrar o salir de pantalla completa cambia el tamaño del libro por completo: zoom al 100%
  useEffect(() => {
    reiniciarZoom(false);
  }, [isFullscreen, reiniciarZoom]);

  // Cuando cambia el espacio disponible, pedir a la librería que recalcule sus límites
  useEffect(() => {
    if (!area.w) return undefined;
    const t = setTimeout(() => {
      try { flipBookRef.current?.pageFlip()?.update(); } catch (_) {}
    }, 60);
    reajustarZoom(); // el hueco cambió: re-limitar el desplazamiento, sin perder el zoom
    return () => clearTimeout(t);
  }, [area.w, area.h, reajustarZoom]);

  // Atajos de teclado: + acerca, - aleja, 0 vuelve al 100%
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return; // no pisar el zoom del navegador
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomEn(zoom + ZOOM_PASO, null, true); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomEn(zoom - ZOOM_PASO, null, true); }
      else if (e.key === '0') { e.preventDefault(); reiniciarZoom(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, zoomEn, reiniciarZoom]);

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    const fsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;

    if (fsSimulado) { setFsSimulado(false); return; }

    if (fsElement === el) {
      try {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      } catch (err) {
        console.warn('No se pudo salir de pantalla completa:', err);
      }
      return;
    }

    try {
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
      else setFsSimulado(true);
    } catch (err) {
      // Safari en iOS (y navegadores que la bloquean) no conceden la API: overlay a pantalla completa
      setFsSimulado(true);
    }
  }, [fsSimulado]);

  const loadPDF = async (url) => {
    const token = ++cargaRef.current; // descarta una carga vieja si se navega a otra publicación
    try {
      setLoading(true);
      setError(null);

      // Cargar el documento PDF
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;
      if (token !== cargaRef.current) return;
      pdfDocRef.current = pdf; // se conserva vivo para re-rasterizar en alta al ampliar

      // Rasterizar en tandas: como mucho CONCURRENCIA_RENDER canvas vivos a la vez.
      // El índice fija el orden, así que las páginas quedan en su sitio pase lo que pase.
      const total = pdf.numPages;
      const renderedPages = new Array(total);
      let siguiente = 0;
      const trabajador = async () => {
        for (let i = siguiente++; i < total; i = siguiente++) {
          if (token !== cargaRef.current) return;
          renderedPages[i] = await renderPage(pdf, i + 1);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCIA_RENDER, total) }, trabajador),
      );

      if (token !== cargaRef.current) {
        revocarImagenes(renderedPages); // llegó tarde: soltar lo rasterizado
        return;
      }

      revocarImagenes(imagenesRef.current); // soltar las del PDF anterior
      imagenesRef.current = renderedPages;
      setPages(renderedPages);
      setLoading(false);
    } catch (err) {
      if (token !== cargaRef.current) return;
      console.error('Error loading PDF:', err);
      setError('Error al cargar el PDF. Verifica que el archivo existe.');
      setLoading(false);
    }
  };

  const renderPage = async (pdf, pageNumber) => {
    try {
      const page = await pdf.getPage(pageNumber);
      const viewportBase = page.getViewport({ scale: 1.0 });
      const renderScale = 2.0;
      const viewport = page.getViewport({ scale: renderScale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = { canvasContext: context, viewport };
      await page.render(renderContext).promise;
      // Blob + objectURL en vez de toDataURL: el base64 pesa 33% más y queda como string en el
      // heap de JS retenido por el estado. Las URLs se revocan al cambiar de PDF y al desmontar.
      const imageDataUrl = await new Promise((resolver, rechazar) => {
        canvas.toBlob(
          (blob) => (blob ? resolver(URL.createObjectURL(blob)) : rechazar(new Error('canvas sin blob'))),
          FORMATO_PAGINA,
          CALIDAD_PAGINA,
        );
      });
      // Soltar el búfer del canvas ya (a 2x son ~8 MB por página), sin esperar al recolector
      canvas.width = 0;
      canvas.height = 0;

      const annotations = await page.getAnnotations();
      const links = [];
      for (const annotation of annotations) {
        if (annotation.subtype === 'Link') {
          // Manejar enlaces externos
          const linkUrl = annotation.url || annotation.unsafeUrl || null;
          // Manejar enlaces internos (destinos)
          const dest = annotation.dest || null;
          
          // Necesitamos al menos una URL o un destino
          if (!linkUrl && !dest) continue;
          if (!annotation.rect) continue;
          
          // Si hay destino interno, resolver a qué página apunta
          let destPageNumber = null;
          if (dest) {
            try {
              // Obtener el destino
              let destination = dest;
              if (typeof dest === 'string') {
                destination = await pdf.getDestination(dest);
              }
              
              if (destination && destination[0]) {
                // destination[0] es una referencia a la página
                const destRef = destination[0];
                destPageNumber = await pdf.getPageIndex(destRef) + 1; // +1 porque pageIndex es base 0
              }
            } catch (e) {
              console.warn('Error resolviendo destino interno:', dest, e);
            }
          }
          // Convertir rect a coordenadas del viewport escalado directamente (ya con origen arriba-izquierda)
          // Nota: convertToViewportRectangle devuelve [x1, y1, x2, y2] en coordenadas CSS del viewport actual
          let vpRect;
          try {
            vpRect = viewport.convertToViewportRectangle(annotation.rect.slice());
          } catch (e) {
            vpRect = null;
          }
          if (!vpRect) continue;
          let [vx1, vy1, vx2, vy2] = vpRect;
          // Normalizar (pueden venir invertidos)
          const leftPx = Math.min(vx1, vx2);
          const rightPx = Math.max(vx1, vx2);
          const topPx = Math.min(vy1, vy2);
          const bottomPx = Math.max(vy1, vy2);
          let w = rightPx - leftPx;
          let h = bottomPx - topPx;
          const originalH = h;
          // Heurística: si es una línea de texto muy fina, aplicar ajuste vertical hacia abajo
          let verticalFudge = 0;
          if (h <= 18) { // probable línea de texto (depende de escala)
            verticalFudge = 3; // desplazar 3px hacia abajo
            // mantén la parte superior y extiende hacia abajo
            h = h + verticalFudge;
          }
          // Expandir hacia abajo adicional si la altura queda muy pequeña todavía
          if (h < 12) {
            h += 4;
          }
          const pct = {
            left: (leftPx / viewport.width) * 100,
            top: ((topPx - (-verticalFudge)) / viewport.height) * 100, // topPx no cambia (el desplazamiento se refleja solo en la altura extra)
            width: (w / viewport.width) * 100,
            height: (h / viewport.height) * 100
          };
          links.push({
            url: linkUrl,
            dest: dest,
            destPageNumber: destPageNumber,
            pdfRect: annotation.rect.slice(),
            viewportRect: [leftPx, topPx, leftPx + w, topPx + h],
            pct,
            meta: { originalH, verticalFudge }
          });
        }
      }

      return {
        pageNumber,
        imageUrl: imageDataUrl,
        width: viewport.width,
        height: viewport.height,
        pdfWidth: viewportBase.width,
        pdfHeight: viewportBase.height,
        renderScale,
        links
      };
    } catch (err) {
      console.error(`Error rendering page ${pageNumber}:`, err);
      return null;
    }
  };

  // Memoizar la lista de páginas para no desencadenar reconciliaciones agresivas del DOM del flipbook
  const flipBookPages = useMemo(() => {
    return pages.map((pageData, idx) => (
      <Page key={pageData?.pageNumber ?? idx} pageData={pageData} className="page" flipBookRef={flipBookRef} />
    ));
  }, [pages]);

  /*
   * Geometría del libro.
   *
   * react-pageflip (size="stretch" + autoSize) deriva TODO el tamaño del ancho de su
   * contenedor: la altura sale de `alto/ancho` de las props y el modo (una página vs.
   * doble página) de `ancho contenedor < 2 * minWidth`. Además congela esos ajustes al
   * construirse. Por eso aquí calculamos la caja exacta que cabe en el hueco medido y se
   * la imponemos al contenedor; la relación de aspecto se toma del PDF para no deformar.
   */
  const geometria = useMemo(() => {
    const primera = pages[0];
    const arPagina = primera && primera.height ? primera.width / primera.height : 0.773;
    // Páginas apaisadas se leen mejor de a una; las verticales, como libro abierto (salvo en móvil)
    const unaPagina = isMobile || arPagina >= 1;
    const arLibro = unaPagina ? arPagina : arPagina * 2;
    const ancho = Math.max(0, Math.floor(Math.min(area.w, area.h * arLibro)));
    const alto = Math.round(ancho / arLibro);
    // El modo se fija con minWidth al construir: lo escalonamos para no recrear en cada píxel
    const escalon = Math.max(2, Math.floor(ancho / 60));
    return {
      unaPagina,
      ancho,
      alto,
      escalon,
      anchoMin: unaPagina ? escalon * 60 : 100,
      arPaginaPct: Math.round(arPagina * 1000),
    };
  }, [pages, isMobile, area]);

  /*
   * Numeración mostrada en la barra, al estilo Issuu: "5 / 28" con una sola página y
   * "6-7 / 28" cuando se ven dos.
   *
   * `onFlip` entrega el índice de la PRIMERA página del pliego visible (en la librería,
   * `currentPageIndex = spread[0]`). Con `showCover` los pliegos son [0], [1,2], [3,4]...,
   * así que la portada y una eventual última página impar van solas.
   */
  const numeracion = useMemo(() => {
    const total = pages.length;
    if (!total) return null;
    const i = Math.min(Math.max(paginaActual, 0), total - 1);
    const enPareja = !geometria.unaPagina && i > 0 && i + 1 < total;
    return {
      texto: enPareja ? `${i + 1}-${i + 2} / ${total}` : `${i + 1} / ${total}`,
      // posición de lectura en 0..1, para marcar el punto activo del indicador
      avance: total > 1 ? i / (total - 1) : 0,
      // números de página a la vista (1-based), para rasterizarlas en alta al ampliar
      visibles: enPareja ? [i + 1, i + 2] : [i + 1],
    };
  }, [pages.length, paginaActual, geometria.unaPagina]);

  // Al ampliar, la página visible se vuelve a rasterizar desde el PDF a la resolución que
  // realmente pide esta pantalla (ver el hook: depende del tamaño en pantalla y del dpr)
  usePaginasNitidas({
    pdfRef: pdfDocRef,
    pages,
    visibles: numeracion ? numeracion.visibles : VACIO,
    zoom,
    contenedorRef: containerRef,
    anchoPagina: geometria.unaPagina ? geometria.ancho : geometria.ancho / 2,
    zoomMax: ZOOM_MAX,
  });

  // La librería congela sus ajustes al construirse: hay que recrearla cuando cambian de verdad
  const claveInstancia =`${geometria.unaPagina ? 'p' : 'd'}-${geometria.arPaginaPct}-${geometria.unaPagina ? geometria.escalon : 0}-${pages.length}`;

  // Al recrearse, destruir la instancia anterior (react-pageflip no lo hace y deja listeners vivos)
  useEffect(() => {
    const instancia = flipBookRef.current?.pageFlip?.();
    return () => {
      try { if (instancia) instancia.destroy(); } catch (_) {}
    };
  }, [claveInstancia]);

  const flipBookComponent = useMemo(() => {
    const primera = pages[0];
    if (!primera || geometria.ancho <= 0) return null;
    return (
      <HTMLFlipBook
        key={claveInstancia}
        ref={flipBookRef}
        // Sólo importa la proporción: el tamaño real lo da el ancho del contenedor
        width={primera.pdfWidth || primera.width}
        height={primera.pdfHeight || primera.height}
        size="stretch"
        minWidth={geometria.anchoMin}
        maxWidth={5000}
        minHeight={100}
        maxHeight={5000}
        maxShadowOpacity={0.5}
        showCover={true}
        mobileScrollSupport={true}
        className="flipbook"
        startPage={paginaActualRef.current}
        onFlip={(e) => { paginaActualRef.current = e.data; setPaginaActual(e.data); reiniciarZoom(); }}
        drawShadow={true}
        flippingTime={600}
        usePortrait={true}
        startZIndex={10}
        autoSize={true}
        clickEventForward={false}
        useMouseEvents={true}
        swipeDistance={30}
        showPageCorners={true}
        disableFlipByClick={false}
      >
        {flipBookPages}
      </HTMLFlipBook>
    );
  }, [flipBookPages, pages, geometria, claveInstancia, reiniciarZoom]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" style={{ minHeight: 260 }} role="status" aria-live="polite">
        <div className="w-full max-w-lg bg-gray-900 text-white rounded-xl shadow-2xl p-8 flex items-center gap-6">
          <div className="flex-shrink-0">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent border-white/60" aria-hidden="true"></div>
          </div>

          <div>
            <h3 className="text-2xl font-semibold">Cargando documento...</h3>
            <p className="text-gray-300 mt-2">Por favor espera mientras preparamos las páginas para su visualización.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-400 text-center">
          <p className="text-xl mb-2">❌ Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-300">No se encontraron páginas en el PDF.</p>
      </div>
    );
  }

  return (
    // Contenedor principal del "Reader" con fondo gris
    <div
      ref={containerRef}
      className={`reader-container shadow-2xl ${isFullscreen ? 'fullscreen' : ''} ${isMobile ? 'movil' : ''}`}
    >
      {/* Escenario: flecha | libro | flecha. El alto lo reparte flex, sin cálculos manuales */}
      <div className="reader-stage">

        {/* Flecha izquierda - oculta en móvil */}
        {!isMobile && (
          <button
            onClick={() => irAPagina('flipPrev')}
            className="nav-button"
            aria-label="Página anterior"
            style={{ width: isFullscreen ? '64px' : '48px', height: isFullscreen ? '64px' : '48px' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Hueco disponible: se mide con ResizeObserver y de ahí sale el tamaño del libro */}
        <div className="flipbook-wrapper" ref={setAreaEl}>
          {/* Ventana del zoom: conserva el tamaño calculado y recorta lo que se sale al ampliar */}
          <div
            ref={refViewport}
            className={`flipbook-viewport ${conZoom ? 'con-zoom' : ''} ${arrastrando ? 'arrastrando' : ''}`}
            style={{ width: geometria.ancho || undefined }}
          >
            <div className="flipbook-box" style={estiloTransform}>
              {flipBookComponent}
            </div>
          </div>
        </div>

        {/* Flecha derecha - oculta en móvil */}
        {!isMobile && (
          <button
            onClick={() => irAPagina('flipNext')}
            className="nav-button"
            aria-label="Página siguiente"
            style={{ width: isFullscreen ? '64px' : '48px', height: isFullscreen ? '64px' : '48px' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

      </div>

      {/* Barra de controles inferior (debajo del contenido) */}
      <div className="enhanced-control-bar">
        {/* Slot izquierdo: control de zoom */}
        <div className="control-slot-left">
          <div className="zoom-control" role="group" aria-label="Control de zoom">
            <button
              type="button"
              className="zoom-btn"
              onClick={() => zoomEn(zoom - ZOOM_PASO, null, true)}
              disabled={zoom <= ZOOM_MIN}
              title="Alejar"
              aria-label="Alejar"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path strokeLinecap="round" strokeWidth={2.2} d="M6 12h12" />
              </svg>
            </button>

            <input
              type="range"
              className="zoom-slider"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.05}
              value={zoom}
              /* sin transición: al arrastrar el deslizador llegan muchos cambios seguidos */
              onChange={(e) => zoomEn(parseFloat(e.target.value))}
              title={`Zoom ${Math.round(zoom * 100)}%`}
              aria-label="Nivel de zoom"
            />

            <button
              type="button"
              className="zoom-btn"
              onClick={() => zoomEn(zoom + ZOOM_PASO, null, true)}
              disabled={zoom >= ZOOM_MAX}
              title="Acercar"
              aria-label="Acercar"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path strokeLinecap="round" strokeWidth={2.2} d="M12 6v12M6 12h12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Centro: página actual sobre el total, y el avance de lectura */}
        <div className="page-info-center">
          <div className="page-counter" aria-live="polite">
            {numeracion ? numeracion.texto : ''}
          </div>
          <div className="progress-indicator">
            <div className="progress-dots">
              {(() => {
                const cantidad = Math.min(pages.length, 10);
                // El punto activo mapea el avance de lectura sobre los puntos disponibles
                const activo = cantidad > 1 && numeracion
                  ? Math.round(numeracion.avance * (cantidad - 1))
                  : 0;
                return Array.from({ length: cantidad }, (_, i) => (
                  <div key={i} className={`dot ${i === activo ? 'activo' : ''}`}></div>
                ));
              })()}
              {pages.length > 10 && <span className="more-indicator">...</span>}
            </div>
          </div>
        </div>
        
        {/* Slot derecho para futuros controles */}
        <div className="control-slot-right">
          <button
            className="barra-btn"
            title="Volver a la primera página"
            aria-label="Volver a la primera página"
            type="button"
            onClick={irAlInicio}
            disabled={paginaActual === 0}
          >
            {/* Glifo ⏮ macizo: en contorno la barra y la punta se tocan y se lee como una "K" */}
            <svg
              fill="currentColor"
              stroke="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
              preserveAspectRatio="xMidYMid meet"
            >
              <rect x="5.5" y="6" width="2.4" height="12" rx="1.2" />
              <path d="M18.5 7.1a1 1 0 0 0-1.54-.84l-6.9 4.9a1 1 0 0 0 0 1.68l6.9 4.9a1 1 0 0 0 1.54-.84z" />
            </svg>
          </button>

          <button
            className="barra-btn"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            type="button"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              /* Icono SALIR fullscreen: mismas proporciones y grosor que el de entrar */
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                preserveAspectRatio="xMidYMid meet"
              >
                {/* Cuatro esquinas internas (colapso) */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H5v4M15 5h4v4M9 19H5v-4M15 19h4v-4"
                />
              </svg>
            ) : (
              /* Icono ENTRAR fullscreen: cuatro esquinas externas (expansión) */
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
      {/* Floating debug export button */}
      {/* Debug export button removed for production */}
    </div>
  );
};

export default Flipbook;

// Debugging helpers removed for production build